// Dotted curved SVG lines from the top-bar connector chips to the department pods of the current
// floor that use them; a line pulses when an agent of that department uses the connector.
import { svg, clear } from '../dom.js';
import { deptColor } from '../format.js';
import { floorDepartments } from '../store.js';
import { hasLibraryPrefix } from './connectorpolicy.js';

const PULSE_MS = 2400;

/**
 * True for the server name of a connector of the office's library (`oc-…`, or `pc-…` for one
 * added before the library existed; bare, or inside an MCP tool name "mcp__oc-…__tool") (pure).
 * The prefixes are reserved: no detected connector ever starts with them. Such a connector is
 * credited by its exact key only (its label may be the name of a detected one: two Canva).
 * @param {string} keyOrTool
 */
export function isLibraryConnector(keyOrTool) {
  const s = String(keyOrTool ?? '').trim();
  const m = /^mcp__(.+?)(?:__|$)/.exec(s);
  return hasLibraryPrefix(m ? m[1] : s);
}

function norm(s) {
  return String(s ?? '').toLowerCase().replace(/^claude\.ai[\s_]+/, '').replace(/[^a-z0-9]/g, '');
}

/**
 * Finds the connector key an agent activity refers to (pure). Accepts a full MCP tool name
 * ("mcp__claude_ai_Gmail__search"), a server key, or a loose name ("Gmail", "ClickUp").
 * @param {string} activity
 * @param {Array<{key: string, name?: string}>} connectors
 * @returns {string|null}
 */
export function matchConnector(activity, connectors) {
  const a = String(activity ?? '').trim();
  if (!a || !Array.isArray(connectors) || !connectors.length) return null;
  const m = /^mcp__(.+?)__/.exec(a) || /^mcp__(.+)$/.exec(a);
  const needle = m ? m[1] : a;
  const exact = connectors.find((c) => c.key === needle);
  if (exact) return exact.key;
  // A library connector ("mcp__oc-…__search-designs") matches its exact key only: never a detected
  // « Canva » through a loose name.
  if (hasLibraryPrefix(needle)) return null;
  const n = norm(needle);
  if (!n) return null;
  // Loose names never reach a library connector either (its label is free text).
  const loose = connectors.filter((c) => !c.library && !hasLibraryPrefix(c.key));
  const byNorm = loose.find((c) => norm(c.key) === n || norm(c.name) === n);
  if (byNorm) return byNorm.key;
  const partial = loose.find((c) => {
    const k = norm(c.name || c.key);
    return k.length >= 3 && (n.includes(k) || k.includes(n));
  });
  return partial ? partial.key : null;
}

/**
 * Which (connector, department) pairs of a floor are linked (pure). A department uses a connector
 * when one of its agents has it in `connectors` ({server}). Only when the agent's connectors are
 * not visible to this user (no `connectors` field) does a matching name in `suggested` count: an
 * agent whose connectors are known but empty uses none (no line to a pod that cannot use it).
 * @returns {Array<{key: string, deptId: string}>}
 */
export function deptConnectorLinks({ departments = [], agents = [], connectors = [], floorId }) {
  const out = [];
  const seen = new Set();
  const add = (key, deptId) => {
    const id = `${key}|${deptId}`;
    if (!seen.has(id)) { seen.add(id); out.push({ key, deptId }); }
  };
  const keys = new Set(connectors.map((c) => c.key));
  for (const d of floorDepartments(departments, floorId)) {
    for (const a of agents) {
      if (a.deptId !== d.id) continue;
      if (Array.isArray(a.connectors)) {
        for (const c of a.connectors) if (keys.has(c?.server)) add(c.server, d.id);
      } else if (Array.isArray(a.suggested)) {
        for (const s of a.suggested) {
          const key = matchConnector(s, connectors);
          if (key) add(key, d.id);
        }
      }
    }
  }
  return out;
}

/**
 * A soft cubic curve from a chip (x1, y1) down to a pod anchor (x2, y2) (pure).
 * @returns {string} SVG path data
 */
export function curvePath(x1, y1, x2, y2) {
  const dy = Math.max(40, y2 - y1);
  const bend = (x2 - x1) * 0.18;
  const c1x = x1 + bend;
  const c1y = y1 + dy * 0.55;
  const c2x = x2 - bend * 0.4;
  const c2y = y2 - dy * 0.45;
  const f = (n) => n.toFixed(1);
  return `M${f(x1)} ${f(y1)} C${f(c1x)} ${f(c1y)} ${f(c2x)} ${f(c2y)} ${f(x2)} ${f(y2)}`;
}

/**
 * Creates the lines controller.
 * @param {object} ctx app context
 * @param {{svgEl: SVGSVGElement, getChipRects: () => Map<string, DOMRect>, getOrigin: () => {left: number, top: number}}} opts
 * @returns {{render(): void, layout(): void, update(anchors: object[]): void, pulse(key: string, deptId: string): void, destroy(): void}}
 */
export function createConnectorLines(ctx, { svgEl, getChipRects, getOrigin }) {
  const { store } = ctx;
  const group = svg('g', { class: 'conn-lines' });
  svgEl.appendChild(group);
  const lines = new Map();
  let chipRects = new Map();
  let origin = { left: 0, top: 0 };
  let enabled = true;

  function makeLine(key, deptId, temporary) {
    const d = store.get().office.departments.find((x) => x.id === deptId);
    const color = deptColor(d?.color, deptId);
    const path = svg('path', { class: 'conn-line', fill: 'none' });
    const end = svg('circle', { class: 'conn-end', r: 2.6 });
    const g = svg('g', { class: temporary ? 'conn-link is-temp' : 'conn-link', dataset: { key, dept: deptId } }, path, end);
    g.style.setProperty('--conn', color);
    group.appendChild(g);
    return { key, deptId, g, path, end, d: '', temporary, timer: null };
  }

  /** Recomputes which lines exist (floor, departments, agents or connectors changed). */
  function render() {
    const s = store.get();
    enabled = s.ui.view !== 'building' && !s.ui.sceneFallback;
    svgEl.classList.toggle('is-hidden', !enabled);
    const links = enabled ? deptConnectorLinks({ ...s.office, connectors: s.connectors, floorId: s.ui.floorId }) : [];
    const keep = new Set();
    for (const l of links) {
      const id = `${l.key}|${l.deptId}`;
      keep.add(id);
      const existing = lines.get(id);
      if (!existing) lines.set(id, makeLine(l.key, l.deptId, false));
      else if (existing.temporary) { existing.temporary = false; existing.g.classList.remove('is-temp'); }
    }
    for (const [id, line] of lines) {
      if (!keep.has(id) && !line.temporary) { line.g.remove(); lines.delete(id); }
    }
    layout();
    update(lastAnchors);
  }

  /** Re-reads the chip positions and the scene origin (call on resize / top bar changes). */
  function layout() {
    chipRects = getChipRects() || new Map();
    origin = getOrigin() || { left: 0, top: 0 };
    for (const line of lines.values()) line.d = '';
  }

  let lastAnchors = [];
  function update(anchors) {
    lastAnchors = Array.isArray(anchors) ? anchors : [];
    if (!enabled || !lines.size) return;
    const byDept = new Map();
    for (const a of anchors) if (a.kind === 'dept') byDept.set(a.id, a);
    for (const line of lines.values()) {
      const chip = chipRects.get(line.key);
      const a = byDept.get(line.deptId);
      const visible = !!chip && !!a && a.visible !== false;
      line.g.classList.toggle('is-off', !visible);
      if (!visible) continue;
      const x1 = chip.left + chip.width / 2;
      const y1 = chip.bottom + 2;
      // End on the pod itself when the scene gives it (podX/podY), else on the card anchor.
      const x2 = origin.left + (Number.isFinite(a.podX) ? a.podX : a.x);
      const y2 = origin.top + (Number.isFinite(a.podY) ? a.podY : a.y);
      const d = curvePath(x1, y1, x2, y2);
      if (d === line.d) continue;
      line.d = d;
      line.path.setAttribute('d', d);
      line.end.setAttribute('cx', x2.toFixed(1));
      line.end.setAttribute('cy', y2.toFixed(1));
    }
  }

  /** Animates the line between a connector and a department (creates a temporary one if needed). */
  function pulse(key, deptId) {
    if (!enabled || !key || !deptId) return;
    const id = `${key}|${deptId}`;
    let line = lines.get(id);
    if (!line) {
      if (!chipRects.has(key)) return;
      line = makeLine(key, deptId, true);
      lines.set(id, line);
      update(lastAnchors); // draw it now: an idle scene may not push anchors before the pulse ends
    }
    line.g.classList.remove('is-pulse');
    // Restart the CSS animation on the next frame.
    requestAnimationFrame(() => line.g.classList.add('is-pulse'));
    clearTimeout(line.timer);
    line.timer = setTimeout(() => {
      line.g.classList.remove('is-pulse');
      if (line.temporary) { line.g.remove(); lines.delete(id); }
    }, PULSE_MS);
  }

  let lastFloorKey = '';
  const offs = [
    store.on('office', render),
    store.on('connectors', render),
    store.on('ui', (ui) => {
      const key = `${ui.view}|${ui.floorId}|${ui.sceneFallback}`;
      if (key !== lastFloorKey) { lastFloorKey = key; render(); }
    }),
  ];
  render();
  return {
    render, layout, update, pulse,
    destroy() {
      for (const off of offs) off();
      for (const line of lines.values()) clearTimeout(line.timer);
      clear(group);
      group.remove();
      lines.clear();
    },
  };
}
